import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { DATE_FORMAT_MMMM_YYYY, LOCALE_CA, LOCALE_ES } from '@app/@core/constants/common.constants';
import { TransformNumberPipe } from '@app/@shared/pipes/transform-number.pipe';
import { ExcelService } from '@app/@shared/services/print/excel.service';
import { TranslateService } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import moment from 'moment';
import { ControlPanelStore } from '../control-panel-service/control-panel.store';
import {
  InvestmentByProductsDTO,
  InvestmentProductsDTO,
  TotalInvestmentProductsDTO,
} from '../models/control-panel.model';

const monthsTitle = 0;
const totalTitle = 1;
@Component({
  selector: 'scf-invest-product',
  templateUrl: './invest-product.component.html',
  styleUrls: ['./invest-product.component.scss'],
})
export class InvestProductComponent implements OnInit {
  displayedColumns: string[];

  dataSource = new MatTableDataSource<InvestmentProductsDTO[] | TotalInvestmentProductsDTO[]>();
  dataSrc = new MatTableDataSource<any>();
  private data: InvestmentByProductsDTO;

  formData: FormGroup;
  private division: string;
  private user: string;

  private dataRow: string[];
  private totalRow: string[];
  descriptionHeaders: Array<string[]> = [];

  constructor(
    private excelService: ExcelService,
    private controlPanelStore: ControlPanelStore,
    private translateService: TranslateService,
    private transformNumberPipe: TransformNumberPipe
  ) {}

  ngOnInit(): void {
    this.descriptionHeaders[monthsTitle] = [
      'inversorProduct.percentageTotal',
      'inversorProduct.numOperations',
      'inversorProduct.financePral',
      'inversorProduct.incentive',
      'inversorProduct.protection',
    ];

    this.descriptionHeaders[totalTitle] = [
      'inversorProduct.numOperations',
      'inversorProduct.totalInvestment',
      'inversorProduct.financePral',
      'inversorProduct.financeSegs',
      'inversorProduct.incentive',
      'inversorProduct.protection',
    ];

    this.dataRow = [...this.descriptionHeaders[monthsTitle]];
    this.totalRow = [...this.descriptionHeaders[totalTitle]];
  }

  getElement(element: any) {
    console.log(element);
  }

  sendFormData(data: any) {
    this.user = data?.user;
    this.division = data?.division;
    this.formData = data?.form;
    this.getInvestorProductReport();
  }

  private getInvestorProductReport() {
    const dataArray: any[] = [];
    this.dataSrc.data = [];
    this.controlPanelStore.getInvestorProductReport(this.formData).then(
      (data) => {
        if (data) {
          if (Object.keys(data).length > 0) {
            data.invProductsbyMonthYear.forEach((invProductMonth) => {
              dataArray.push(invProductMonth);
            });
            data.invAccumulated.forEach((invAccum) => {
              dataArray.push(invAccum);
            });
            this.dataSrc.data = dataArray;
            this.data = data;

            //For pdf and excel
            if (this.dataRow.length === 6 && this.totalRow.length === 7) {
              this.dataRow.shift();
              this.totalRow.shift();
            }
            this.dataRow.unshift(data.invProductsbyMonthYear[0]?.financialProduct);
            this.totalRow.unshift(data.invProductsbyMonthYear[0]?.totals.financialProduct);
          }
        }
      },
      (error) => console.error(error)
    );
  }

  private loadDataTable(): void {
    this.displayedColumns = [this.translateService.instant('inversorProduct.product')];
    this.dataSource.data = [];
    const data = this.data;

    if (data) {
      const inv = [...data.invProductsbyMonthYear];
      const totals: TotalInvestmentProductsDTO[] = [];

      data.invProductsbyMonthYear.forEach((investment) => {
        totals.push(investment.totals);
        if (investment.monthYear) {
          this.displayedColumns.push(investment.monthYear);
        }
      });
      this.displayedColumns.push(this.translateService.instant('inversorProduct.acum'));

      data.invAccumulated.forEach((investment) => {
        inv.push(investment);
        totals.push(investment.totals);
      });

      this.dataSource.data = [inv, totals];
    }
  }

  createPdf() {
    try {
      this.loadDataTable();
      let doc = new jsPDF({
        orientation: 'landscape',
        format: [296, 209],
      });

      const date = moment().locale('es');
      const filename =
        this.translateService.instant('excel.inversionProduct.filename') + moment().locale('es').format('DDMMYYYY');

      doc.setProperties({
        title: filename,
      });
      let dataPdf: any[] = [];

      this.dataSource.data.forEach((item, index) => {
        const productPDFRow: string[] = [];
        if (index === this.dataSource.data.length - 1) {
          //Description
          productPDFRow.push(this.concatTextBreak(this.totalRow));
          //Data
          item.forEach((product) => {
            const productPDF: string =
              '\n' +
              this.transformNumberPipe.transform(product.numOperations) +
              '\n' +
              this.transformNumberPipe.transform(product.totalInvest) +
              '\n' +
              this.transformNumberPipe.transform(product.mainFunding) +
              '\n' +
              this.transformNumberPipe.transform(product.financialSegs) +
              '\n' +
              this.transformNumberPipe.transform(product.incentive) +
              '\n' +
              this.transformNumberPipe.transform(product.protections);
            productPDFRow.push(productPDF);
          });
        } else {
          productPDFRow.push(this.concatTextBreak(this.dataRow));
          item.forEach((product) => {
            const productPDF: string =
              '\n' +
              this.transformNumberPipe.transform(product.overTotal) +
              '\n' +
              this.transformNumberPipe.transform(product.numOperations) +
              '\n' +
              this.transformNumberPipe.transform(product.mainFunding) +
              '\n' +
              this.transformNumberPipe.transform(product.incentive) +
              '\n' +
              this.transformNumberPipe.transform(product.protections);
            productPDFRow.push(productPDF);
          });
        }
        dataPdf.push(productPDFRow);
      });

      autoTable(doc, {
        startY: 55,
        head: [this.displayedColumns],
        headStyles: { fillColor: '#969696' },
        showHead: 'firstPage',
        body: dataPdf.map(Object.values),
        didDrawPage: (data) => {
          doc.setFontSize(24);
          doc.setTextColor('1E90FF');

          let text = this.translateService.instant('excel.dashboard.title');
          if (text.length <= 27) doc.text(text, 160, 25);
          else doc.text(text, 140, 25);

          doc.setTextColor('#000000');
          doc.setFontSize(16);
          doc.text(this.translateService.instant('excel.inversionProduct.list'), 104, 52);

          doc.setFontSize(12);
          doc.text(
            'De ' + this.getInitialDate(DATE_FORMAT_MMMM_YYYY) + ' a ' + this.getFinalDate(DATE_FORMAT_MMMM_YYYY),
            110,
            37
          );

          doc.text(this.user, 14, 45);
          doc.text(this.translateService.instant('excel.division') + this.division, 14, 52);
          doc.text(this.translateService.instant('excel.date') + date.format('DD-MM-YYYY HH:mm:ss'), 220, 37);
          doc.text(this.translateService.instant('excel.pag') + data.pageNumber, 220, 45);

          var img = new Image();
          img.src = 'assets/img/logo.PNG';
          doc.addImage(img, 'png', 3, 10, 80, 30);
        },
      });

      var blob = doc.output('blob');
      FileSaver.saveAs(blob, filename);
      window.open(URL.createObjectURL(blob));
    } catch (error) {
      console.error(error);
    }
  }

  createExcel() {
    this.loadDataTable();
    let dataForExcel: any[] = [];
    const filename =
      this.translateService.instant('excel.inversionProduct.filename') + moment().locale('es').format('DDMMYYYY');
    const date = [this.getInitialDate(DATE_FORMAT_MMMM_YYYY), this.getFinalDate(DATE_FORMAT_MMMM_YYYY)];

    this.dataSource.data.forEach((item, index) => {
      const rowProduct: string[] = [];
      const rowpercentageTotal: string[] = [];
      const rowNumOperations: string[] = [];
      const rowFinancePral: string[] = [];
      const rowIncentive: string[] = [];
      const rowProtection: string[] = [];

      const rowTotal: string[] = [];
      const rowNumOperationsTotal: string[] = [];
      const rowInversionTotal: string[] = [];
      const rowFinanPral: string[] = [];
      const rowFinanSegs: string[] = [];
      const rowIncentiveTotal: string[] = [];
      const rowProtectionTotal: string[] = [];

      if (index === this.dataSource.data.length - 1) {
        rowTotal.push(item[0].financialProduct);
        rowNumOperationsTotal.push(this.translateService.instant('inversorProduct.numOperations'));
        rowInversionTotal.push(this.translateService.instant('inversorProduct.totalInvestment'));
        rowFinanPral.push(this.translateService.instant('inversorProduct.financePral'));
        rowFinanSegs.push(this.translateService.instant('inversorProduct.financeSegs'));
        rowIncentiveTotal.push(this.translateService.instant('inversorProduct.incentive'));
        rowProtectionTotal.push(this.translateService.instant('inversorProduct.protection'));

        item.forEach((product) => {
          rowNumOperationsTotal.push(
            this.excelService.checkNumbersFormat(product.numOperations.toString().replace('.', ','))
          );
          rowInversionTotal.push(
            this.excelService.checkNumbersFormat(product.totalInvest.toString().replace('.', ','))
          );
          rowFinanPral.push(this.excelService.checkNumbersFormat(product.mainFunding.toString().replace('.', ',')));
          rowFinanSegs.push(this.excelService.checkNumbersFormat(product.financialSegs.toString().replace('.', ',')));
          rowIncentiveTotal.push(this.excelService.checkNumbersFormat(product.incentive.toString().replace('.', ',')));
          rowProtectionTotal.push(
            this.excelService.checkNumbersFormat(product.protections.toString().replace('.', ','))
          );
        });

        dataForExcel.push(rowTotal);
        dataForExcel.push(rowNumOperationsTotal);
        dataForExcel.push(rowInversionTotal);
        dataForExcel.push(rowFinanPral);
        dataForExcel.push(rowFinanSegs);
        dataForExcel.push(rowIncentiveTotal);
        dataForExcel.push(rowProtectionTotal);
      } else {
        rowProduct.push(item[0].financialProduct);
        rowpercentageTotal.push(this.translateService.instant('inversorProduct.percentageTotal'));
        rowNumOperations.push(this.translateService.instant('inversorProduct.numOperations'));
        rowFinancePral.push(this.translateService.instant('inversorProduct.financePral'));
        rowIncentive.push(this.translateService.instant('inversorProduct.incentive'));
        rowProtection.push(this.translateService.instant('inversorProduct.protection'));

        item.forEach((product) => {
          rowpercentageTotal.push(product.overTotal.toString().replace('.', ',') + '%');
          rowNumOperations.push(
            this.excelService.checkNumbersFormat(product.numOperations.toString().replace('.', ','))
          );
          rowFinancePral.push(this.excelService.checkNumbersFormat(product.mainFunding.toString().replace('.', ',')));
          rowIncentive.push(this.excelService.checkNumbersFormat(product.incentive.toString().replace('.', ',')));
          rowProtection.push(this.excelService.checkNumbersFormat(product.protections.toString().replace('.', ',')));
        });

        dataForExcel.push(rowProduct);
        dataForExcel.push(rowpercentageTotal);
        dataForExcel.push(rowNumOperations);
        dataForExcel.push(rowFinancePral);
        dataForExcel.push(rowIncentive);
        dataForExcel.push(rowProtection);
      }
    });
    const excelData = {
      fileName: filename,
      headers: this.displayedColumns,
      data: dataForExcel,
      division: this.division,
      user: this.user,
      date: date,
    };
    this.excelService.exportExcelInvestProduct(excelData, this.dataSource.data[0].length);
  }

  private concatTextBreak(text: string[]): string {
    return text.map((value) => this.translateService.instant(value)).join('\n');
  }

  private getInitialDate(format: string) {
    return this.formData
      ?.get('initialDate')
      ?.value.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }

  private getFinalDate(format: string) {
    return this.formData
      ?.get('finalDate')
      ?.value.locale(this.translateService.currentLang === LOCALE_ES ? LOCALE_ES : LOCALE_CA)
      .format(format);
  }
}
